/** @jsxImportSource @emotion/react */
import { FlagIcon } from '@kaiizen/kai-shiki/react';
import { ComponentType } from 'react';
import { DEFAULT_NEEDS_ATTENTION_INDICATOR_STYLES, NeedsAttentionIndicatorStyles } from './needsAttentionIndicatorStyles';

/**
 * A flag glyph with a reliably-hoverable tooltip.
 *
 * The `title` attribute does not reliably produce a native tooltip when set
 * directly on an `<svg>` — the wrapping `<span>` is what carries it, along
 * with the `role`/`aria-label` that make the icon an accessible image rather
 * than decoration. `FlagIcon` always renders at `currentColor`, so `active`
 * is what actually colors it — `styles.active` layers onto `styles.root`.
 */
export const NeedsAttentionIndicator = ({
  show = true,
  active = true,
  Icon = FlagIcon,
  size,
  label,
  styles = DEFAULT_NEEDS_ATTENTION_INDICATOR_STYLES,
}: {
  /** Whether to render at all. Defaults to always-shown. */
  show?: boolean;
  /** Whether this reads as flagged (its `active` styles) or muted/inherited. */
  active?: boolean;
  Icon?: ComponentType<{ size?: number; color?: string }>;
  size: number;
  /** Used for both the tooltip and the accessible name. */
  label: string;
  styles?: NeedsAttentionIndicatorStyles;
}) => {
  if (!show) return null;

  return (
    <span css={[styles.root, active && styles.active]} role='img' title={label} aria-label={label}>
      {Icon && <Icon size={size} color='currentColor' />}
    </span>
  );
};
